import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, X, Send, KeyRound, GitPullRequestArrow } from "lucide-react";
import { Panel, Pill } from "./ui";
import { nexsysApi } from "./api";

const BUCKETS = [
  { key: "passing", label: "Passing", color: "#34d399" },
  { key: "attention", label: "Attention required", color: "#ff9f43" },
  { key: "failing", label: "Failing", color: "#ff3b6b" },
  { key: "stale", label: "Stale", color: "#8b93a7", note: "Excluded from current status" },
  { key: "contested", label: "Contested", color: "#f97316", note: "Unresolved conflict", conflict: true },
  { key: "indeterminate", label: "Indeterminate", color: "#a78bfa", note: "Needs determination" },
  { key: "escalated", label: "Escalated", color: "#ec4899", note: "Governance review required" },
];
const COLOR = Object.fromEntries(BUCKETS.map((b) => [b.key, b.color]));
const NOTE = Object.fromEntries(BUCKETS.filter((b) => b.note).map((b) => [b.key, b.note]));
const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

function TraceRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/[0.05] py-1.5">
      <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-white/35">{label}</span>
      <span className="break-all text-right font-mono text-[11px] text-white/75">{value ?? "—"}</span>
    </div>
  );
}

function Drilldown({ row, onClose }) {
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="cp-drilldown">
      <div className="nx-scroll max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0e1a] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-bold text-white">{row.title}</div>
            <div className="font-mono text-[9px] tracking-widest text-white/35">TRACEABILITY · GOVERNED VANTA CONTEXT</div>
          </div>
          <button onClick={onClose} data-testid="cp-drilldown-close" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="mb-3 flex items-center gap-2">
          <Pill tone="cyan">{String(row.bucket).toUpperCase()}</Pill>
          {NOTE[row.bucket] && <span className="text-[11px] text-white/50">{NOTE[row.bucket]}</span>}
        </div>
        <TraceRow label="Canonical object" value={row.canonical_object} />
        <TraceRow label="Vanta external ID" value={row.external_object_id} />
        <TraceRow label="Source" value={row.source} />
        <TraceRow label="Reported status" value={row.reported_status} />
        <TraceRow label="Reconciliation" value={`${row.reconciliation_status} — ${row.reconciliation_detail || ""}`} />
        <TraceRow label="Policy" value={row.policy} />
        <TraceRow label="Observed at" value={fmt(row.observed_at)} />
        <TraceRow label="Effective at" value={fmt(row.effective_at)} />
        <TraceRow label="Retrieved (last sync)" value={fmt(row.retrieved_at)} />
        <TraceRow label="Provenance" value={row.provenance ? `${row.provenance.provider}/${row.provenance.transport} · ${row.provenance.external_id}` : "—"} />
        <TraceRow label="Envelope" value={row.envelope_id} />
      </div>
    </div>
  );
}

export function CompliancePosture() {
  const [posture, setPosture] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const isAdmin = localStorage.getItem("nexsys_gate_role") === "admin";

  const load = useCallback(() => { nexsysApi.vantaPosture().then(setPosture).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const submitEligible = async () => {
    setBusy(true);
    try {
      const r = await nexsysApi.vantaSubmitEligible();
      setSubmitMsg(`${r.submitted}/${r.eligible} submitted — ${r.note}`);
    } catch { setSubmitMsg("Submission rejected by the governed evidence path."); }
    setBusy(false);
  };

  const pending = posture && !posture.enabled && posture.controls_monitored === 0;
  const c = posture?.counts || {};

  return (
    <Panel title="Compliance Posture" subtitle="GOVERNED · RECONCILED VANTA CONTEXT — NOT A CERTIFICATION CLAIM"
      testid="compliance-posture-card"
      right={<Pill tone={pending ? "amber" : "cyan"}>{pending ? "CREDENTIALS PENDING" : "GOVERNED"}</Pill>}>
      {!posture && <div className="py-6 text-center font-mono text-[11px] text-white/35">Loading governed compliance context…</div>}

      {pending && (
        <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="cp-pending">
          <KeyRound className="h-7 w-7 text-amber-300/70" />
          <p className="max-w-md text-sm text-white/70">Vanta integration configured — credentials required for live compliance context.</p>
          <p className="max-w-md text-[11px] text-white/40">
            No simulated data is shown. Once credentials are supplied, compliance context flows Vanta → VantaContextConnector →
            CEM mapping → reconciliation → EDIOS-governed state → this card.
          </p>
        </div>
      )}

      {posture && !pending && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center" data-testid="cp-metric-monitored">
              <div className="font-display text-lg font-bold text-white">{posture.controls_monitored}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">Monitored</div>
            </div>
            {BUCKETS.map((b) => (
              <div key={b.key} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center" data-testid={`cp-metric-${b.key}`} title={b.note || ""}>
                <div className="flex items-center justify-center gap-1 font-display text-lg font-bold" style={{ color: b.color }}>
                  {b.conflict && (c[b.key] || 0) > 0 && <AlertTriangle className="h-3.5 w-3.5" />}
                  {c[b.key] || 0}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">{b.label}</div>
                {b.note && (c[b.key] || 0) > 0 && <div className="mt-0.5 text-[9px]" style={{ color: b.color }}>{b.note}</div>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
            <div data-testid="cp-framework"><span className="text-white/35">Framework: </span><span className="text-white/75">{(posture.frameworks || []).join(", ") || "Not reported"}</span></div>
            <div data-testid="cp-last-sync"><span className="text-white/35">Last synchronized: </span><span className="text-white/75">{fmt(posture.last_synchronized)}</span></div>
            <div data-testid="cp-evidence-freshness"><span className="text-white/35">Evidence freshness: </span><span className="text-white/75">{posture.evidence_freshness?.label}{posture.evidence_freshness?.last_sent_at ? ` (${fmt(posture.evidence_freshness.last_sent_at)})` : ""}</span></div>
            <div data-testid="cp-open-risks"><span className="text-white/35">Open compliance risks: </span><span className="text-white/75">{posture.open_risks}</span></div>
          </div>

          {posture.controls_monitored === 0 ? (
            <p className="text-[12px] text-white/45" data-testid="cp-no-controls">No reconciled compliance controls yet — awaiting first governed synchronization. No sample data is displayed.</p>
          ) : (
            <div className="nx-scroll max-h-44 space-y-1 overflow-y-auto" data-testid="cp-controls-list">
              {posture.controls.map((r) => (
                <button key={r.envelope_id} onClick={() => setSel(r)} data-testid={`cp-control-row-${r.external_object_id}`}
                  className="flex w-full items-center gap-2 rounded-lg border border-white/[0.05] bg-white/[0.02] px-2.5 py-1.5 text-left transition-colors hover:border-cyan-400/30">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: COLOR[r.bucket] }} />
                  <span className="truncate text-[11px] text-white/70">{r.title}</span>
                  <span className="ml-auto shrink-0 font-mono text-[9px] uppercase" style={{ color: COLOR[r.bucket] }}>
                    {r.bucket === "indeterminate" ? "Needs determination" : r.bucket === "escalated" ? "Governance review required" : r.bucket}
                  </span>
                </button>
              ))}
            </div>
          )}

          {(posture.recent_signals || []).length > 0 && (
            <div data-testid="cp-signals">
              <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] tracking-widest text-white/35">
                <GitPullRequestArrow className="h-3 w-3" /> CHANGES SINCE PREVIOUS SYNCHRONIZATION · ENTERPRISE SIGNALS
              </div>
              {posture.recent_signals.slice(0, 4).map((s) => (
                <div key={s.id} className="text-[11px] text-white/60">
                  {s.external_object_id}: <span className="text-white/80">{s.prior_status} → {s.new_status}</span>
                  <span className="ml-2 font-mono text-[9px] text-amber-300/70">{(s.evaluation || {}).status === "pending" ? "AWAITING SIGNAL INTELLIGENCE" : (s.evaluation || {}).warrants_decision ? "CANDIDATE DECISION PROPOSED" : "RECORDED AS EVIDENCE"}</span>
                </div>
              ))}
            </div>
          )}

          {isAdmin && (
            <div className="flex items-center gap-3 border-t border-white/[0.06] pt-3">
              <button onClick={submitEligible} disabled={busy} data-testid="cp-submit-evidence"
                className="flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-[11px] text-cyan-200 transition-colors hover:bg-cyan-400/20 disabled:opacity-50">
                {busy ? <ShieldCheck className="h-3.5 w-3.5 animate-pulse" /> : <Send className="h-3.5 w-3.5" />}
                Submit Eligible Evidence
              </button>
              {submitMsg && <span className="text-[10px] text-white/45" data-testid="cp-submit-result">{submitMsg}</span>}
            </div>
          )}
        </div>
      )}

      {sel && <Drilldown row={sel} onClose={() => setSel(null)} />}
    </Panel>
  );
}
